import {
  DefaultRenderingPipeline,
  ImageProcessingConfiguration,
  SSAO2RenderingPipeline,
  type Camera,
  type Scene,
} from "@babylonjs/core";

/**
 * Post-processing stack. Art direction (see docs/Castaway Plan.md): gentle
 * bloom and a warm-leaning tonemap rather than a cold, high-contrast
 * "AAA shooter" grade — most of the warmth actually comes from
 * DayNightCycle's light colors, so grading here stays restrained rather
 * than double-warming on top of that.
 */
export class PostProcessPipeline {
  readonly defaultPipeline: DefaultRenderingPipeline;
  readonly ssaoPipeline: SSAO2RenderingPipeline | null;

  constructor(scene: Scene, camera: Camera) {
    this.defaultPipeline = new DefaultRenderingPipeline("mainPipeline", true, scene, [camera]);
    this.defaultPipeline.bloomEnabled = true;
    this.defaultPipeline.bloomThreshold = 0.7;
    this.defaultPipeline.bloomWeight = 0.25;
    this.defaultPipeline.bloomKernel = 48;
    this.defaultPipeline.fxaaEnabled = true;

    this.defaultPipeline.imageProcessing.toneMappingEnabled = true;
    this.defaultPipeline.imageProcessing.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
    this.defaultPipeline.imageProcessing.exposure = 0.95;
    this.defaultPipeline.imageProcessing.contrast = 1.05;

    // Not every fallback render path supports SSAO2 — degrade gracefully
    // rather than throwing (this app must not hard-fail on WebGL2).
    this.ssaoPipeline = SSAO2RenderingPipeline.IsSupported
      ? new SSAO2RenderingPipeline("ssao", scene, { ssaoRatio: 0.5, blurRatio: 0.5 }, [camera])
      : null;
    if (this.ssaoPipeline) {
      this.ssaoPipeline.totalStrength = 0.8;
      this.ssaoPipeline.radius = 1.2;
    } else {
      console.warn("[Castaway] SSAO2 not supported on this render path; continuing without it.");
    }
  }

  dispose(): void {
    this.defaultPipeline.dispose();
    this.ssaoPipeline?.dispose();
  }
}
